// Presentational, auth-light print route for the Timeline PDF slide deck.
//
// Renders the whole deck as stacked 4:3 pages for Chromium's page.pdf() to
// paginate. Data comes from `window.__DECK_DATA__`, injected by the export
// script (apps/app/scripts/export-timeline-pdf.ts) via Playwright addInitScript
// — so the headless job needs no session/auth. When the payload is absent the
// route shows a hint (useful when opening the URL directly in a browser).
//
// After the deck mounts it sets `window.__DECK_READY__ = true` so the export
// script can wait for a fully laid-out deck before capturing the PDF.

import { useEffect, useMemo } from 'react';

import { TooltipProvider } from '../components/Tooltip.tsx';
import { buildDeckLayout } from '../deck-layout.ts';

import '../deck.css';
import { DeckPage, type DeckPageData } from './DeckPage.tsx';

export interface DeckData {
  org: string;
  /** Repository name(s) for the H2 (e.g. "Homebase1"). Falls back to `org`. */
  repos?: string;
  pages: DeckPageData[];
}

declare global {
  interface Window {
    __DECK_DATA__?: DeckData;
    __DECK_READY__?: boolean;
  }
}

export function TimelineDeckView() {
  const data = typeof window !== 'undefined' ? window.__DECK_DATA__ : undefined;

  // Chronological order → first-appearance axis order.
  const pages = useMemo(
    () => (data ? [...data.pages].sort((a, b) => a.date.localeCompare(b.date)) : []),
    [data],
  );
  const layout = useMemo(() => (pages.length ? buildDeckLayout(pages) : null), [pages]);

  useEffect(() => {
    if (layout) {
      // Signal the export script that the deck is laid out and safe to print.
      window.__DECK_READY__ = true;
    }
  }, [layout]);

  if (!data || !layout || pages.length === 0) {
    return (
      <div className='deck-root' data-deck-empty>
        <div className='deck-slide' style={{ display: 'grid', placeItems: 'center', padding: 40 }}>
          <div style={{ textAlign: 'center', maxWidth: 520, fontFamily: 'var(--sans)' }}>
            <h1 className='snap-h1'>Timeline deck</h1>
            <p className='snap-subtitle'>
              No deck data injected. This route renders a PDF export from
              <code> window.__DECK_DATA__</code>, set by the export script
              (<code>apps/app/scripts/export-timeline-pdf.ts</code>).
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    // MatrixCell (reused by DeckMatrix) reads a TooltipProvider; this route sits
    // outside the app's authed shell, so provide one here. Tooltips never fire in
    // the static/PDF render, but the hook must resolve.
    <TooltipProvider>
      <div className='deck-root' data-deck-ready>
        {pages.map((page, i) => {
          const axes = layout.pages[i];
          if (!axes) return null;
          return (
            <DeckPage
              key={`${page.date}-${i}`}
              repos={data.repos ?? data.org}
              data={page}
              layout={layout}
              axes={axes}
              pageNumber={i + 1}
              totalPages={pages.length}
              isFirst={i === 0}
            />
          );
        })}
      </div>
    </TooltipProvider>
  );
}
